import type { ContractDepartmentTeam, ContractResponsibilities } from "@closer/shared";
import { Badge, Button, FormField, Modal, Select, useToast } from "@closer/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { api, ApiRequestError } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { tenantKey } from "../../lib/tenantQuery";

/**
 * ÇALIŞAN EKİPLER — D02 (CLOS-1226 · Departmanlar Kanonu §19, §20).
 *
 * ### ⚠ Kullanıcı teknik terim görmüyor
 *
 * §21/§37: arayüzde `ORGANIZATIONAL_POOL`, `ALL_ACTIVE_MEMBERS`, `LEAD` gibi ifadeler
 * geçmez. Kullanıcı "Ana ekip", "Birlikte çalışan ekip", "Tüm departman", "Belirli üyeler"
 * görür. Teknik adı göstermek, kullanıcıyı ürünün iç sözlüğünü öğrenmeye zorlardı.
 *
 * ### ⚠ Ana ekip YILDIZLA ayrışıyor (§19)
 *
 * İki ekip türü aynı görünseydi "bu sözleşmenin asıl sahibi kim?" sorusu listede
 * cevapsız kalırdı.
 *
 * ### ⚠ LEGACY alan ikinci bir liste DEĞİL (kilitli karar)
 *
 * Eski "İlgili departman" satırları, ana ekranı kirleten ikinci bir departman listesi
 * olarak gösterilmiyor. Küçük bir "Geçmiş bilgi" satırı ve yanında tek bir dönüştürme
 * butonu — kullanıcı isterse açık bir kararla çalışan ekibe çevirir.
 */

type Props = {
  contractId: string;
  tenantId: string;
  kayitYazma: boolean;
  /** Legacy "İlgili departman" satırları — okunuyor, yazılmıyor. */
  legacy: ContractResponsibilities["departments"];
};

export function ContractWorkingTeamsSection({ contractId, tenantId, kayitYazma, legacy }: Props) {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [ekleAcik, setEkleAcik] = useState(false);
  const [donusturulecek, setDonusturulecek] = useState<string | null>(null);
  const [uyeKutusu, setUyeKutusu] = useState<ContractDepartmentTeam | null>(null);

  const ekipler = useQuery({
    queryKey: tenantKey(tenantId, "contract-department-teams", contractId),
    queryFn: async () =>
      (
        await api<ContractDepartmentTeam[]>(`/api/contracts/${contractId}/department-teams`, {
          tenantId,
        })
      ).data,
  });

  const tazele = async () => {
    await queryClient.invalidateQueries({
      queryKey: tenantKey(tenantId, "contract-department-teams", contractId),
    });
    await queryClient.invalidateQueries({
      queryKey: tenantKey(tenantId, "contract-responsibilities", contractId),
    });
  };

  const hata = (error: unknown) =>
    toast.error(
      error instanceof ApiRequestError ? error.message : "İşlem tamamlanamadı, tekrar deneyin.",
    );

  const ekle = useMutation({
    mutationFn: async (govde: { departmentId: string; participationType: string }) => {
      await api(`/api/contracts/${contractId}/department-teams`, {
        method: "POST",
        body: JSON.stringify(govde),
        tenantId,
      });
    },
    onSuccess: async () => {
      setEkleAcik(false);
      await tazele();
      toast.success("Ekip eklendi");
    },
    onError: hata,
  });

  const cikar = useMutation({
    mutationFn: async (participationId: string) => {
      await api(`/api/contracts/${contractId}/department-teams/${participationId}`, {
        method: "DELETE",
        tenantId,
      });
    },
    onSuccess: async () => {
      await tazele();
      toast.success("Ekip çıkarıldı");
    },
    onError: hata,
  });

  const donustur = useMutation({
    mutationFn: async (govde: { responsibilityId: string; participationType: string }) => {
      await api(
        `/api/contracts/${contractId}/responsibilities/departments/${govde.responsibilityId}/convert-to-team`,
        {
          method: "POST",
          body: JSON.stringify({ participationType: govde.participationType }),
          tenantId,
        },
      );
    },
    onSuccess: async () => {
      setDonusturulecek(null);
      await tazele();
      toast.success("Geçmiş departman bilgisi çalışan ekibe dönüştürüldü");
    },
    onError: hata,
  });

  const kapsam = useMutation({
    mutationFn: async (govde: { participationId: string; memberScope: string }) => {
      await api(
        `/api/contracts/${contractId}/department-teams/${govde.participationId}/member-scope`,
        { method: "PUT", body: JSON.stringify({ memberScope: govde.memberScope }), tenantId },
      );
    },
    onSuccess: async () => {
      await tazele();
      toast.success("Üye kapsamı güncellendi");
    },
    onError: hata,
  });

  const uyeEkle = useMutation({
    mutationFn: async (govde: {
      participationId: string;
      membershipId: string;
      /** ⚠ `selected` kapsamı daraltır, `scoped` dışarıdan birini davet eder — ayrı uçlar. */
      tur: "selected" | "scoped";
    }) => {
      const yol = govde.tur === "selected" ? "selected-members" : "scoped-members";
      await api(`/api/contracts/${contractId}/department-teams/${govde.participationId}/${yol}`, {
        method: "POST",
        body: JSON.stringify({ membershipId: govde.membershipId }),
        tenantId,
      });
    },
    onSuccess: async () => {
      setUyeKutusu(null);
      await tazele();
      toast.success("Üye eklendi");
    },
    onError: hata,
  });

  const uyeCikar = useMutation({
    mutationFn: async (govde: {
      participationId: string;
      membershipId: string;
      scopedMemberId: string | null;
    }) => {
      /**
       * ⚠ İKİ AYRI UÇ, çünkü iki ayrı kayıt: sözleşmeye özel üyelik kendi satırını
       * sonlandırıyor, seçili üye ise kapsam listesinden çıkıyor. Tek uca indirseydik
       * "hangi bağ koptu?" sorusu denetimde belirsiz kalırdı.
       */
      if (govde.scopedMemberId) {
        await api(`/api/contracts/${contractId}/scoped-members/${govde.scopedMemberId}`, {
          method: "DELETE",
          tenantId,
        });
        return;
      }
      await api(
        `/api/contracts/${contractId}/department-teams/${govde.participationId}/selected-members/${govde.membershipId}`,
        { method: "DELETE", tenantId },
      );
    },
    onSuccess: async () => {
      await tazele();
      toast.success("Üye çıkarıldı");
    },
    onError: hata,
  });

  const yonetebilir = can("contract.share") && kayitYazma;
  const satirlar = ekipler.data ?? [];

  return (
    <section
      className="rounded-lg border border-border bg-surface p-4"
      aria-label="Çalışan Ekipler"
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <h2 className="text-base font-medium text-fg-strong">Çalışan Ekipler</h2>
          <p className="pt-1 text-sm text-fg-muted">
            Bu sözleşme üzerinde hangi ekiplerin çalıştığını gösterir. Ekip eklemek, o ekibin
            üyelerine sözleşmeyi görme erişimi verir.
          </p>
        </div>
        {yonetebilir ? (
          <Button type="button" size="sm" variant="secondary" onClick={() => setEkleAcik(true)}>
            Ekip ekle
          </Button>
        ) : null}
      </div>

      {satirlar.length === 0 ? (
        <p className="pt-4 text-sm text-fg-muted">Henüz çalışan ekip eklenmemiş.</p>
      ) : (
        <ul className="flex flex-col gap-2 pt-4">
          {satirlar.map((ekip) => (
            <li
              key={ekip.id}
              data-testid="calisan-ekip"
              className="flex items-center justify-between gap-2"
            >
              <span className="flex items-center gap-2 text-sm text-fg">
                {/* ⚠ Ana ekip yıldızla ayrışıyor (§19). */}
                {ekip.participationType === "lead" ? (
                  <span aria-hidden="true" className="text-warning">
                    ★
                  </span>
                ) : null}
                {ekip.departmentName}
                <Badge tone={ekip.participationType === "lead" ? "info" : "neutral"}>
                  {ekip.participationType === "lead" ? "Ana ekip" : "Birlikte çalışan ekip"}
                </Badge>
                {/* ⚠ Kapsam yalnız DARALTILMIŞSA gösteriliyor: varsayılan teknik bir ayar
                    olarak görünmemeli (§7.1). */}
                {ekip.memberScope === "selected_members" ? (
                  <Badge tone="neutral">Belirli üyeler</Badge>
                ) : null}
                {ekip.departmentArchived ? <Badge tone="warning">Arşivli</Badge> : null}
              </span>
              {yonetebilir ? (
                <span className="flex items-center gap-1">
                  {/*
                    ⚠ KAPSAM GELİŞMİŞ BİR SEÇİM (§7.1/§37): varsayılan görünür bir teknik
                    ayar olmak zorunda değil. Tek düğme iki durum arasında geçiyor; ayrı bir
                    "gelişmiş ayarlar" ekranı sıradan kullanımı ağırlaştırırdı.
                  */}
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    loading={kapsam.isPending}
                    onClick={() =>
                      kapsam.mutate({
                        participationId: ekip.id,
                        memberScope:
                          ekip.memberScope === "selected_members"
                            ? "all_active_members"
                            : "selected_members",
                      })
                    }
                  >
                    {ekip.memberScope === "selected_members"
                      ? "Tüm departmana aç"
                      : "Belirli üyelerle sınırla"}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setUyeKutusu(ekip)}
                  >
                    Üyeler
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    aria-label={`${ekip.departmentName} ekibini çıkar`}
                    loading={cikar.isPending}
                    onClick={() => cikar.mutate(ekip.id)}
                  >
                    Çıkar
                  </Button>
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {/*
        ⚠ GEÇMİŞ BİLGİ (kilitli karar): ikinci bir departman listesi DEĞİL. Küçük bir satır
        ve tek bir dönüştürme butonu. Satır yoksa bu blok hiç görünmüyor — ölü bir başlık
        bırakmak, emekli bir kavramı diri göstermek olurdu.
      */}
      {legacy.length > 0 ? (
        <div className="mt-4 border-t border-border pt-3" data-testid="gecmis-departman">
          {legacy.map((d) => (
            <div key={d.id} className="flex items-center justify-between gap-2">
              <span className="text-xs text-fg-muted">
                Geçmiş bilgi: İlgili departman — {d.departmentName ?? "—"}
              </span>
              {yonetebilir ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setDonusturulecek(d.id)}
                >
                  Çalışan ekibe dönüştür
                </Button>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}

      <EkipEkleModal
        open={ekleAcik}
        tenantId={tenantId}
        onClose={() => setEkleAcik(false)}
        onConfirm={(govde) => ekle.mutate(govde)}
        isPending={ekle.isPending}
      />

      <UyeEkleModal
        ekip={uyeKutusu}
        tenantId={tenantId}
        contractId={contractId}
        yonetebilir={yonetebilir}
        onClose={() => setUyeKutusu(null)}
        onConfirm={(govde) =>
          uyeKutusu ? uyeEkle.mutate({ participationId: uyeKutusu.id, ...govde }) : undefined
        }
        onRemove={(govde) =>
          uyeKutusu ? uyeCikar.mutate({ participationId: uyeKutusu.id, ...govde }) : undefined
        }
        isPending={uyeEkle.isPending || uyeCikar.isPending}
      />

      <Modal
        open={donusturulecek !== null}
        onClose={() => setDonusturulecek(null)}
        title="Çalışan ekibe dönüştür"
        footer={
          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={() => setDonusturulecek(null)}>
              Vazgeç
            </Button>
            <Button
              type="button"
              loading={donustur.isPending}
              onClick={() =>
                donusturulecek
                  ? donustur.mutate({
                      responsibilityId: donusturulecek,
                      participationType: "collaborating",
                    })
                  : undefined
              }
            >
              Dönüştür
            </Button>
          </div>
        }
      >
        {/*
          ⚠ ERİŞİM ETKİSİ AÇIKÇA SÖYLENİYOR (§23, kilitli karar). Sessizce dönüştürseydik,
          kullanıcının bir etiketi düzeltiyorum sandığı işlem bir departmanın tamamına
          erişim açardı.
        */}
        <p className="text-sm text-fg">
          Bu departman <strong>Birlikte çalışan ekip</strong> olarak eklenecek ve departmanın aktif
          üyeleri bu sözleşmeyi görebilecek.
        </p>
        <p className="pt-2 text-sm text-fg-muted">
          Geçmiş bilgi satırı kaldırılacak. Bu işlem kayıt altına alınır.
        </p>
      </Modal>
    </section>
  );
}

/** Ekip ekleme akışı (§20.1). */
function EkipEkleModal({
  open,
  tenantId,
  onClose,
  onConfirm,
  isPending,
}: {
  open: boolean;
  tenantId: string;
  onClose: () => void;
  onConfirm: (govde: { departmentId: string; participationType: string }) => void;
  isPending: boolean;
}) {
  const [departmentId, setDepartmentId] = useState("");
  const [tur, setTur] = useState("collaborating");

  const departmanlar = useQuery({
    queryKey: tenantKey(tenantId, "departments"),
    enabled: open,
    queryFn: async () =>
      (
        await api<{ id: string; name: string; archivedAt: string | null }[]>("/api/departments", {
          tenantId,
        })
      ).data,
  });

  /**
   * ⚠ ARŞİVLİ DEPARTMAN SEÇENEK OLARAK SUNULMUYOR.
   *
   * Sunucu `/api/departments` yanıtına arşivlileri kiracı konfigürasyonuna göre katabiliyor;
   * listeyi olduğu gibi göstermek, kullanıcıya sunucunun 409 ile reddedeceği bir seçenek
   * sunmak olurdu. `ContractDepartmentDialog` ile aynı çizgi — kural sunucuda DA var
   * (`addContractDepartmentTeam`), bu yalnız kullanıcıyı boş bir denemeden koruyor.
   */
  const atanabilir = (departmanlar.data ?? []).filter((d) => d.archivedAt === null);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Ekip ekle"
      footer={
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Vazgeç
          </Button>
          <Button
            type="button"
            loading={isPending}
            disabled={!departmentId}
            onClick={() => onConfirm({ departmentId, participationType: tur })}
          >
            Ekle
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-3">
        <FormField label="Departman">
          <Select value={departmentId} onChange={(e) => setDepartmentId(e.target.value)}>
            <option value="">Seçin</option>
            {atanabilir.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </Select>
        </FormField>
        <FormField label="Ekip türü">
          {/*
            ⚠ Varsayılan "Birlikte çalışan": ana ekip sözleşmenin asıl sahibini belirler ve
            sistem o kararı kullanıcı adına veremez. Ana ekip zaten varsa sunucu 409 döner.
          */}
          <Select value={tur} onChange={(e) => setTur(e.target.value)}>
            <option value="collaborating">Birlikte çalışan ekip</option>
            <option value="lead">Ana ekip</option>
          </Select>
        </FormField>
        <p className="text-xs text-fg-muted">
          Ekip eklemek, departmanın aktif üyelerine bu sözleşmeyi görme erişimi verir. Rol ve
          yetkiler değişmez.
        </p>
      </div>
    </Modal>
  );
}

/**
 * EKİP ÜYESİ EKLEME (§7.2 seçili üye · §8 sözleşmeye özel üye).
 *
 * ### ⚠ İki farklı işlem, tek kutu ama AYRI cümleler
 *
 * "Departman üyesi" seçimi kapsamı DARALTIR — departmanın kendi üyelerinden seçim.
 * "Sözleşmeye özel" ise dışarıdan birini DAVET eder ve o kişinin gerçek departmanı
 * DEĞİŞMEZ (§8.3). İkisini tek bir "üye ekle" cümlesine indirseydik, kullanıcı gerçek
 * departman üyeliğini değiştirdiğini sanabilirdi.
 */
function UyeEkleModal({
  ekip,
  tenantId,
  contractId,
  yonetebilir,
  onClose,
  onConfirm,
  onRemove,
  isPending,
}: {
  ekip: ContractDepartmentTeam | null;
  tenantId: string;
  contractId: string;
  yonetebilir: boolean;
  onClose: () => void;
  onConfirm: (govde: { membershipId: string; tur: "selected" | "scoped" }) => void;
  onRemove: (govde: { membershipId: string; scopedMemberId: string | null }) => void;
  isPending: boolean;
}) {
  const [membershipId, setMembershipId] = useState("");
  const [tur, setTur] = useState<"selected" | "scoped">("scoped");

  const ekipUyeleri = useQuery({
    queryKey: tenantKey(tenantId, "contract-team-members", ekip?.id ?? ""),
    enabled: ekip !== null,
    queryFn: async () =>
      (
        await api<
          {
            membershipId: string;
            displayName: string | null;
            provenance: string[];
            scopedMemberId: string | null;
          }[]
        >(`/api/contracts/${contractId}/department-teams/${ekip!.id}/members`, { tenantId })
      ).data,
  });

  const uyeler = useQuery({
    queryKey: tenantKey(tenantId, "directory-members"),
    enabled: ekip !== null,
    queryFn: async () =>
      (
        await api<{ items: { id: string; displayName: string }[] }>(
          "/api/tenants/members?limit=100",
          { tenantId },
        )
      ).data.items,
  });

  return (
    <Modal
      open={ekip !== null}
      onClose={onClose}
      title={ekip ? `${ekip.departmentName} — üye ekle` : "Üye ekle"}
      footer={
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Vazgeç
          </Button>
          <Button
            type="button"
            loading={isPending}
            disabled={!membershipId}
            onClick={() => onConfirm({ membershipId, tur })}
          >
            Ekle
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-3">
        {/*
          ⚠ MEVCUT ÜYELER ÖNCE (§19): kullanıcı kimin zaten ekipte olduğunu görmeden ekleme
          yapmamalı. Yalnız ekleme kutusu gösterseydik, aynı kişiyi ikinci kez eklemeye
          çalışmak sıradan bir hata olurdu.
        */}
        {(ekipUyeleri.data ?? []).length > 0 ? (
          <ul className="flex flex-col gap-1" data-testid="ekip-uyeleri">
            {(ekipUyeleri.data ?? []).map((u) => (
              <li key={u.membershipId} className="flex items-center justify-between gap-2">
                <span className="text-sm text-fg">
                  {u.displayName ?? "—"}
                  {/* ⚠ Köken kullanıcı diliyle: "Sözleşmeye özel" (§19). */}
                  {u.provenance.includes("scoped") ? (
                    <span className="pl-2 text-xs text-fg-muted">· Sözleşmeye özel</span>
                  ) : null}
                </span>
                {yonetebilir && u.provenance.some((p) => p === "scoped" || p === "selected") ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    aria-label={`${u.displayName ?? "Üye"} çıkar`}
                    onClick={() =>
                      onRemove({
                        membershipId: u.membershipId,
                        scopedMemberId: u.scopedMemberId,
                      })
                    }
                  >
                    Çıkar
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-fg-muted">Bu ekipte henüz kimse yok.</p>
        )}

        <FormField label="Üye">
          <Select value={membershipId} onChange={(e) => setMembershipId(e.target.value)}>
            <option value="">Seçin</option>
            {(uyeler.data ?? []).map((u) => (
              <option key={u.id} value={u.id}>
                {u.displayName}
              </option>
            ))}
          </Select>
        </FormField>
        <FormField label="Ekleme türü">
          <Select value={tur} onChange={(e) => setTur(e.target.value as "selected" | "scoped")}>
            <option value="scoped">Sözleşmeye özel — yalnız bu sözleşmede çalışsın</option>
            <option value="selected">Departman üyesi — bu ekipte çalışacaklar arasına al</option>
          </Select>
        </FormField>
        <p className="text-xs text-fg-muted">
          Sözleşmeye özel ekleme kişinin gerçek departmanını değiştirmez ve yalnız bu sözleşmede
          erişim verir.
        </p>
      </div>
    </Modal>
  );
}
